import type { ComplianceTask, TaskAmountExtract } from '../types';

export type TaskPhase = 'prepare' | 'reconcile' | 'finalize';

export interface PhaseSummary {
  prepareComputedCount: number;
  reconciledCount: number;
  filedCount: number;
  total: number;
}

function hasNum(bag: Record<string, number | null> | undefined, key: string): boolean {
  const v = bag?.[key];
  return typeof v === 'number';
}

/** An obligation is "reconciled" when it has a filed.totalDue (or legacy amounts.totalDue). */
function isReconciled(ex: TaskAmountExtract | undefined): boolean {
  return !!ex && (hasNum(ex.filed, 'totalDue') || hasNum(ex.amounts, 'totalDue'));
}

export function firstIncompletePhase(
  task: ComplianceTask,
  extractByObligation: Map<string, TaskAmountExtract>,
): TaskPhase {
  const filings = task.filings ?? [];
  const anyReconciled = filings.some((f) => isReconciled(extractByObligation.get(f.obligationId)));
  if (!anyReconciled) return 'prepare';
  const allReconciled = filings.every((f) => isReconciled(extractByObligation.get(f.obligationId)));
  if (!allReconciled) return 'reconcile';
  return 'finalize';
}

export function phaseSummary(
  task: ComplianceTask,
  extractByObligation: Map<string, TaskAmountExtract>,
): PhaseSummary {
  const filings = task.filings ?? [];
  let prepareComputedCount = 0;
  let reconciledCount = 0;
  let filedCount = 0;
  for (const f of filings) {
    const ex = extractByObligation.get(f.obligationId);
    if (hasNum(ex?.computed, 'totalDue')) prepareComputedCount++;
    if (isReconciled(ex)) reconciledCount++;
    if (f.filedAt) filedCount++;
  }
  return { prepareComputedCount, reconciledCount, filedCount, total: filings.length };
}
